import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';
import { formatoCantidad } from '@/lib/utils/formato-precio';

/**
 * Los números con rótulo de un resumen (mapa de diseño 226, §3): una grilla de
 * celdas pegadas, la cifra en semibold tabular y una frase corta debajo. Se
 * colorea sólo la cifra que pide atención (`tono`). Si se tocan para filtrar
 * (`onClick`), son botones y la elegida (`activa`) queda subrayada en azul.
 */
export function Cifras({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'grid gap-px overflow-hidden rounded-lg border bg-border [grid-template-columns:repeat(auto-fit,minmax(120px,1fr))]',
        className
      )}
      {...props}
    />
  );
}

export type TonoDeCifra = 'neutro' | 'alerta' | 'bloqueo' | 'ok' | 'baja';

const TINTA: Record<TonoDeCifra, string> = {
  neutro: '',
  alerta: 'text-alerta',
  bloqueo: 'text-bloqueo',
  ok: 'text-ok',
  baja: 'text-baja',
};

export function Cifra({
  valor,
  rotulo,
  tono = 'neutro',
  activa = false,
  onClick,
  chica = false,
  className,
  ...props
}: Omit<HTMLAttributes<HTMLElement>, 'onClick'> & {
  /** Un número se escribe con `formatoCantidad` («1.688»); un texto va tal cual («−3 %»). */
  valor: number | string | ReactNode;
  rotulo: ReactNode;
  tono?: TonoDeCifra;
  activa?: boolean;
  onClick?: () => void;
  /** La cifra más chica, para un resumen dentro de una ventana. */
  chica?: boolean;
}) {
  const cuerpo = (
    <>
      <b
        className={cn(
          'block font-semibold leading-tight tracking-tight tabular-nums',
          chica ? 'text-lg' : 'text-2xl',
          TINTA[tono]
        )}
      >
        {typeof valor === 'number' ? formatoCantidad(valor) : valor}
      </b>{' '}
      {/* ^ el espacio es para el nombre accesible («4 los limita una regla», no
          «4los limita una regla»); el rótulo es un bloque y no se ve (ronda final, C9). */}
      <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{rotulo}</span>
    </>
  );
  const clases = cn(
    'min-w-0 bg-card px-3.5 py-2.5 text-left',
    activa && 'shadow-[inset_0_-2px_0_hsl(var(--primary))]',
    className
  );
  if (onClick)
    return (
      <button
        type="button"
        data-tono={tono}
        aria-pressed={activa}
        onClick={onClick}
        className={cn(clases, 'hover:bg-muted/60')}
        {...props}
      >
        {cuerpo}
      </button>
    );
  return (
    <div data-tono={tono} className={clases} {...props}>
      {cuerpo}
    </div>
  );
}
